import DiagramClass from "@semantical/modules/diagram/DiagramClass.ts";
import { Relationship, KnowledgeGraph } from "@semantical/modules/knowledge-graph";
import { AnchorPosition, AnchorMode, computeDefaultAnchor } from "@semantical/modules/diagram/AnchorPosition.ts";

export default class DiagramRelationship {
    source: DiagramClass;
    target: DiagramClass;
    modelRelationship: Relationship;
    sourceAnchor: AnchorPosition | null = null;
    targetAnchor: AnchorPosition | null = null;

    constructor(
        modelRelationship: Relationship,
        source: DiagramClass,
        target: DiagramClass,
        sourceAnchor?: AnchorPosition | null,
        targetAnchor?: AnchorPosition | null
    ) {
        this.source = source;
        this.target = target;
        this.modelRelationship = modelRelationship;
        this.sourceAnchor = sourceAnchor ?? null;
        this.targetAnchor = targetAnchor ?? null;
    }

    /**
     * Compute absolute position from stored relative anchor (side + offset).
     * Uses the actual dimensions from the DiagramClass.
     */
    private computeAbsolutePosition(anchor: AnchorPosition, diagramClass: DiagramClass): AnchorPosition {
        const width = diagramClass.width;
        const height = diagramClass.height;

        let x: number;
        let y: number;

        switch (anchor.side) {
            case 'top':
                x = diagramClass.x + anchor.offset * width;
                y = diagramClass.y;
                break;
            case 'right':
                x = diagramClass.x + width;
                y = diagramClass.y + anchor.offset * height;
                break;
            case 'bottom':
                x = diagramClass.x + anchor.offset * width;
                y = diagramClass.y + height;
                break;
            case 'left':
                x = diagramClass.x;
                y = diagramClass.y + anchor.offset * height;
                break;
        }

        return { ...anchor, x, y };
    }

    /**
     * Get the source anchor position, computing default if not explicitly set.
     * If an anchor was manually set, recompute absolute position from relative side/offset.
     * Uses actual dimensions from the DiagramClass instances.
     * @param mode - The anchor positioning mode ('auto', 'dynamic', or 'centered')
     * @param ignoreFixed - If true, always compute default anchor (ignore stored fixed position)
     */
    getSourceAnchor(mode: AnchorMode = 'auto', ignoreFixed: boolean = false): AnchorPosition {
        if (this.sourceAnchor && !ignoreFixed) {
            // Recompute absolute position in case the class moved
            return this.computeAbsolutePosition(this.sourceAnchor, this.source);
        }
        return computeDefaultAnchor(
            { x: this.source.x, y: this.source.y, width: this.source.width, height: this.source.height },
            { x: this.target.x, y: this.target.y, width: this.target.width, height: this.target.height },
            true,
            mode
        );
    }

    /**
     * Get the target anchor position, computing default if not explicitly set.
     * If an anchor was manually set, recompute absolute position from relative side/offset.
     * Uses actual dimensions from the DiagramClass instances.
     * @param mode - The anchor positioning mode ('auto', 'dynamic', or 'centered')
     * @param ignoreFixed - If true, always compute default anchor (ignore stored fixed position)
     */
    getTargetAnchor(mode: AnchorMode = 'auto', ignoreFixed: boolean = false): AnchorPosition {
        if (this.targetAnchor && !ignoreFixed) {
            // Recompute absolute position in case the class moved
            return this.computeAbsolutePosition(this.targetAnchor, this.target);
        }
        return computeDefaultAnchor(
            { x: this.source.x, y: this.source.y, width: this.source.width, height: this.source.height },
            { x: this.target.x, y: this.target.y, width: this.target.width, height: this.target.height },
            false,
            mode
        );
    }

    /**
     * Update the source anchor position.
     */
    setSourceAnchor(anchor: AnchorPosition) {
        this.sourceAnchor = anchor;
    }

    /**
     * Update the target anchor position.
     */
    setTargetAnchor(anchor: AnchorPosition) {
        this.targetAnchor = anchor;
    }

    /**
     * Reset anchors to computed defaults.
     */
    resetAnchors() {
        this.sourceAnchor = null;
        this.targetAnchor = null;
    }

    /**
     * Check if this relationship was inferred by the inference engine.
     * Inferred relationships are read-only and visually distinct.
     */
    get isInferred(): boolean {
        return this.modelRelationship.isInferred;
    }

    /**
     * Reconnect the source end to a new class.
     * Updates both the diagram class reference and the model relationship subject.
     * Resets the source anchor to compute a fresh default position.
     */
    reconnectSource(newClass: DiagramClass) {
        this.source = newClass;
        this.modelRelationship.subject = newClass.modelElement;
        this.sourceAnchor = null; // Reset to compute new default
    }

    /**
     * Reconnect the target end to a new class.
     * Updates both the diagram class reference and the model relationship object.
     * Resets the target anchor to compute a fresh default position.
     */
    reconnectTarget(newClass: DiagramClass) {
        this.target = newClass;
        this.modelRelationship.object = newClass.modelElement;
        this.targetAnchor = null; // Reset to compute new default
    }

    toJSON() {
        return {
            modelRelationshipRef: this.modelRelationship.id,
            sourceAnchor: this.sourceAnchor,
            targetAnchor: this.targetAnchor
        }
    }

    static fromJSON(
        json: any,
        diagramClassMap: Map<string, DiagramClass>,
        knowledgeGraph: KnowledgeGraph
    ): DiagramRelationship | null {
        const relationship = knowledgeGraph.getRelationshipById(json.modelRelationshipRef);

        if (!relationship) {
            console.debug(`DiagramRelationship.fromJSON: Relationship not found for id ${json.modelRelationshipRef}`);
            return null;
        }

        const sourceDiagramClass = diagramClassMap.get(relationship.subject.uri);
        const targetDiagramClass = diagramClassMap.get(relationship.object.uri);

        if (!sourceDiagramClass || !targetDiagramClass) {
            console.debug('DiagramRelationship.fromJSON: source or target DiagramClass not found');
            return null;
        }

        return new DiagramRelationship(
            relationship,
            sourceDiagramClass,
            targetDiagramClass,
            json.sourceAnchor as AnchorPosition | null,
            json.targetAnchor as AnchorPosition | null
        );
    }
}